/**
 * Serialization utilities for BlockNote editor content.
 *
 * New notes are stored as JSON (BlockNote document blocks).
 * Legacy plain-text notes are detected and parsed as markdown on load.
 * This avoids the lossy markdown round-trip that corrupts checklists.
 */

// These serialization functions work with raw JSON (JSON.parse output) and
// editors of any schema configuration, so we use permissive `any` types
// intentionally. The typed RawBlock/RawEditor from ./types are too
// narrow for deserialized data that hasn't been validated against a schema.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawBlock = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawEditor = any;

/**
 * Detects whether a stored note value is JSON (BlockNote document)
 * or plain text / markdown (legacy).
 */
function isJsonDocument(value: string): boolean {
  const trimmed = value.trimStart();
  return trimmed.startsWith('[');
}

/**
 * Parse a stored note string into BlockNote blocks.
 * - If JSON: deserialize directly (lossless round-trip)
 * - If plain text/markdown: use editor's markdown parser
 */
export async function parseNoteToBlocks(
  editor: RawEditor,
  value: string,
): Promise<RawBlock[]> {
  if (!value) return [];

  if (isJsonDocument(value)) {
    try {
      return JSON.parse(value) as RawBlock[];
    } catch {
      // Malformed JSON — fall through to markdown parsing
    }
  }

  // Legacy plain text or markdown
  return await editor.tryParseMarkdownToBlocks(value);
}

/**
 * Serialize BlockNote document blocks to a string for storage.
 * Uses JSON for lossless round-trip (checklists, formatting preserved).
 */
export function serializeBlocks(blocks: RawBlock[]): string {
  return JSON.stringify(blocks);
}

/**
 * Checks whether an inline content array contains only empty text.
 * BlockNote may serialize an empty paragraph's content as
 * `[{"type":"text","text":"","styles":{}}]` — a non-empty array
 * with a single text node whose text is blank.
 */
function isInlineContentEmpty(content: RawBlock[]): boolean {
  return content.every(
    (ic: RawBlock) => ic.type === 'text' && (!ic.text || !ic.text.trim()),
  );
}

/**
 * Detects whether a note string is "structurally empty" — i.e. it contains
 * no meaningful user content, even though BlockNote may serialize it as
 * `[{"type":"paragraph","props":{...},"content":[{"type":"text","text":"","styles":{}}],"children":[]}]`.
 */
export function isEmptyNoteContent(value: string | null | undefined): boolean {
  if (!value) return true;
  if (!isJsonDocument(value)) return !value.trim();
  try {
    const blocks = JSON.parse(value) as RawBlock[];
    return blocks.every(
      (b: RawBlock) =>
        b.type === 'paragraph' &&
        (!b.content ||
          b.content === '' ||
          (Array.isArray(b.content) && isInlineContentEmpty(b.content))) &&
        (!b.children || b.children.length === 0),
    );
  } catch {
    return false;
  }
}
